import { useCallback } from 'react';
import { createSelector } from 'reselect';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { useBoardSelector } from '../../index.tsx';
import {
	getIsIncrementPlanningBoard,
	getIssues,
	getCardTransitions,
} from '../software/software-selectors.tsx';
import { lastFilteredIssueParentIdSelector } from '../work/work-selectors.tsx';
import type { IssueTypeTransitions } from '../../../model/card-transition/card-transition-types.tsx';
import type { ColumnId } from '../../../model/column/column-types.tsx';

const getParentIdForIssueCreationData = createSelector(
	[lastFilteredIssueParentIdSelector, getIssues, getIsIncrementPlanningBoard],
	(lastFilteredIssueParentId, issues, isIPBoard) => ({
		lastFilteredIssueParentId,
		issues,
		isIPBoard,
	}),
);

export const useGetParentIdForIssueCreation = () => {
	const { lastFilteredIssueParentId, issues, isIPBoard } = useBoardSelector(
		getParentIdForIssueCreationData,
		isEqual,
	);
	return useCallback(
		(projectId: number): number | string | null => {
			if (!isIPBoard) {
				return lastFilteredIssueParentId;
			}
			// for the IP board, we should check if the epic belongs to the project
			const parentIssue = isNil(lastFilteredIssueParentId)
				? null
				: issues[lastFilteredIssueParentId];
			return parentIssue?.projectId === projectId ? lastFilteredIssueParentId : null;
		},
		[isIPBoard, issues, lastFilteredIssueParentId],
	);
};

const getInitialTransitionsForColumnData = createSelector(
	[getCardTransitions],
	(cardTransitions) => cardTransitions,
);

export const useGetInitialTransitionsForColumn = () => {
	const cardTransitions = useBoardSelector(getInitialTransitionsForColumnData, isEqual);
	return useCallback(
		(columnId: ColumnId): IssueTypeTransitions =>
			cardTransitions.columnIssueTypeTransitions[String(columnId)],
		[cardTransitions],
	);
};
